import { GatewayControleQuinquennal } from "@conformite/gateway";
import { useQuery } from "@tanstack/react-query";
import { useRef } from "react";
import { ControleQuinquennalApi } from "../controle-quinquennal.api";
import { controleQuinquennalKeys } from "./controle-quinquennal.keys";

// Polling tant qu'un sous-axe est PENDING, borné au temps écoulé depuis l'entrée
// en PENDING (pas `dataUpdateCount`) — même convention que useCQAxeWithFilesPaginatedQuery.
const PENDING_POLL_INTERVAL_MS = 5_000;
const MAX_PENDING_POLL_DURATION_MS = 10 * 60 * 1000;

/**
 * MR-G — Vérifications IA de l'axe assujetti, indexées par `subAxis`
 * (`'<assujettiId>:<DIMENSION>'` ou `'LISTE'`). Map vide tant que le flag
 * gateway `CQ_ASSUJETTI_AI !== 'on'` : la feature est alors invisible et l'UI
 * strictement identique à aujourd'hui.
 */
export type SubAxisVerificationsMap = Map<
  string,
  GatewayControleQuinquennal.ControleQuinquennalSubAxisVerification
>;

export const useCQAxeVerificationsQuery = (idCQ: string, idAxe: string) => {
  // Horodatage de l'entrée en PENDING, remis à zéro dès qu'aucun sous-axe ne l'est.
  const pendingSinceRef = useRef<number | null>(null);
  return useQuery({
    queryKey: controleQuinquennalKeys.axeVerifications(idCQ, idAxe),
    queryFn: () =>
      ControleQuinquennalApi.listAxeSubAxisVerifications(idCQ, idAxe),
    enabled: !!idCQ && !!idAxe,
    staleTime: 30_000,
    select: (rows): SubAxisVerificationsMap =>
      new Map(rows.map((row) => [row.subAxis, row])),
    refetchInterval: (query) => {
      const hasPending = query.state.data?.some(
        (row) =>
          row.status === GatewayControleQuinquennal.VerificationStatus.PENDING
      );
      if (!hasPending) {
        pendingSinceRef.current = null;
        return false;
      }
      const now = Date.now();
      if (pendingSinceRef.current === null) {
        pendingSinceRef.current = now;
      }
      if (now - pendingSinceRef.current > MAX_PENDING_POLL_DURATION_MS) {
        return false;
      }
      return PENDING_POLL_INTERVAL_MS;
    },
  });
};
